import React, { useEffect, useState, useRef, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    ShieldCheck, Search, RefreshCw, Eye, Pill, FlaskConical, 
    FileText, Monitor, Calendar, Clock, Hospital, User, Filter, AlertCircle
} from 'lucide-react';

// --- КОМПОНЕНТ "УМНОГО" ФИЛЬТРА С ПОИСКОМ (Custom Select) ---
const CustomSearchSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 5);
    const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div onClick={() => setIsOpen(!isOpen)} style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', minHeight: '42px'}}>
                <span style={{color: value ? '#111827' : '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9rem'}}>{selectedLabel}</span>
                <span style={{fontSize: '10px', color: '#9ca3af'}}>▼</span>
            </div>
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 100 }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{position: 'relative'}}>
                            <Search size={14} style={{position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}}/>
                            <input 
                                type="text" placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} 
                                style={{ width: '100%', padding: '8px 8px 8px 28px', borderRadius: '6px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.85rem' }} 
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <div 
                            onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }} 
                            style={{ padding: '10px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', transition: 'background 0.2s' }}
                            onMouseOver={e => e.target.style.background = '#f9fafb'} onMouseOut={e => e.target.style.background = 'transparent'}
                        >
                            {placeholder}
                        </div>
                        {filteredOptions.map(opt => (
                            <div 
                                key={opt.value} 
                                onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }} 
                                style={{ padding: '10px 12px', cursor: 'pointer', borderTop: '1px solid #f3f4f6', background: String(value) === String(opt.value) ? '#eff6ff' : 'transparent', color: '#374151', fontSize: '0.9rem', transition: 'background 0.2s' }}
                                onMouseOver={e => { if (String(value) !== String(opt.value)) e.target.style.background = '#f9fafb' }} 
                                onMouseOut={e => { if (String(value) !== String(opt.value)) e.target.style.background = 'transparent' }}
                            >
                                {opt.label}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && <div style={{ padding: '10px 12px', color: '#9ca3af', textAlign: 'center', fontSize: '0.9rem' }}>Ничего не найдено</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

const LogsAction = () => {
    const [logs, setLogs] = useState([]);
    const [hospitals, setHospitals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useContext(AuthContext);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterHospital, setFilterHospital] = useState('');
    
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

    const [visibleCount, setVisibleCount] = useState(10);

    const fetchLogs = async () => {
        setIsLoading(true);
        try {
            // Загружаем и логи, и больницы параллельно
            const [logsRes, hospRes] = await Promise.all([
                api.get('/logs'),
                api.get('/hospitals').catch(() => ({ data: [] }))
            ]);
            setLogs(logsRes.data || []);
            setHospitals(hospRes.data || []);
        } catch (error) {
            console.error("Ошибка загрузки логов:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        setVisibleCount(10);
    }, [searchQuery, filterRole, filterAction, filterHospital, filterDate]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // --- ЖЕСТКИЙ ФИЛЬТР НА ФРОНТЕНДЕ ДЛЯ ГЛАВВРАЧА ---
    const chiefHospital = user?.role === 'chief_doctor' ? hospitals.find(h => h.chief_doctor_id === user.id) : null;
    
    const allowedLogs = user?.role === 'chief_doctor' 
        ? logs.filter(log => chiefHospital && log.hospital === chiefHospital.name)
        : logs;

    const filteredLogs = allowedLogs.filter(log => {
        const text = searchQuery.toLowerCase();
        const matchSearch = (log.user_name || '').toLowerCase().includes(text) || 
                            (log.patient || '').toLowerCase().includes(text) ||
                            (log.action_type || '').toLowerCase().includes(text) ||
                            (log.action_desc || '').toLowerCase().includes(text);
                            
        const matchRole = !filterRole || log.role === filterRole;
        const matchAction = !filterAction || log.action_type === filterAction;
        const matchHospital = !filterHospital || log.hospital === filterHospital;
        const matchDate = !filterDate || (log.created_at && log.created_at.startsWith(filterDate));

        return matchSearch && matchRole && matchAction && matchHospital && matchDate;
    });

    const visibleLogs = filteredLogs.slice(0, visibleCount);

    const uniqueActions = [...new Set(allowedLogs.map(l => l.action_type).filter(Boolean))];
    const uniqueHospitals = [...new Set(allowedLogs.map(l => l.hospital).filter(Boolean))];

    const roleOptions = [
        { value: 'Врач', label: 'Врачи' },
        { value: 'Пациент', label: 'Пациенты' }
    ];
    const actionOptions = uniqueActions.map(a => ({ value: a, label: a }));
    const hospitalOptions = uniqueHospitals.map(h => ({ value: h, label: h }));

    const getInitials = (name) => {
        if (!name) return '??';
        const parts = name.split(' ');
        return parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0][0];
    };
    
    const formatDate = (isoString) => new Date(isoString).toLocaleDateString('ru-RU');
    const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'});

    const getActionStyle = (type) => {
        if (type?.toLowerCase().includes('рецепт')) return { icon: <Pill size={18}/>, color: 'type-blue' };
        if (type?.toLowerCase().includes('анализ') || type?.toLowerCase().includes('диагноз')) return { icon: <FlaskConical size={18}/>, color: 'type-purple' };
        if (type?.toLowerCase().includes('запис')) return { icon: <FileText size={18}/>, color: 'type-green' };
        if (type?.toLowerCase().includes('профил') || type?.toLowerCase().includes('пациент')) return { icon: <Eye size={18}/>, color: 'type-gray' };
        return { icon: <ShieldCheck size={18}/>, color: 'type-gray' };
    };

    return (
        <div className="content-wrapper logs-action">
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>

            <main className="logs-container">
                
                <header className="logs-header">
                    <div className="header-icon"><ShieldCheck /></div>
                    <div className="header-text">
                        <h1>Логи действий системы</h1>
                        <p>
                            {user?.role === 'chief_doctor' 
                                ? 'Мониторинг действий врачей и пациентов вашей больницы' 
                                : 'Мониторинг всех действий врачей и пациентов'}
                        </p>
                    </div>
                </header>

                <section className="stats-grid">
                    <div className="stat-card">
                        <div><p className="stat-label">Записей найдено</p><p className="stat-value text-blue">{filteredLogs.length}</p></div>
                        <div className="stat-icon icon-blue"><ShieldCheck /></div>
                    </div>
                    <div className="stat-card">
                        <div><p className="stat-label">Действия врачей</p><p className="stat-value text-green">{filteredLogs.filter(l => l.role === 'Врач').length}</p></div>
                        <div className="stat-icon icon-green"><User /></div>
                    </div>
                    <div className="stat-card">
                        <div><p className="stat-label">Действия пациентов</p><p className="stat-value text-purple">{filteredLogs.filter(l => l.role === 'Пациент').length}</p></div>
                        <div className="stat-icon icon-purple"><AlertCircle /></div>
                    </div>
                </section>

                <section className="card" style={{ gap: '0' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Filter size={20} color="#9333ea"/> Фильтры
                            </h2>
                            <p className="card-description" style={{ margin: '4px 0 0 0' }}>Настройте отображение логов</p>
                        </div>
                        <button className="btn-outline" onClick={fetchLogs} style={{ display: 'flex', flex: 'none', gap: '8px', alignItems: 'center', padding: '8px 16px', borderRadius: '8px', borderColor: '#d1d5db', cursor: 'pointer' }}>
                            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} color="#4b5563" /> 
                            <span style={{ color: '#4b5563', fontWeight: '500' }}>Обновить</span>
                        </button>
                    </div>
                    
                    <div className="filters-body" style={{ padding: '20px', gap: '0' }}>
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 300px', position: 'relative' }}>
                                <Search className="search-icon" size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                <input 
                                    type="text" 
                                    placeholder="Поиск по имени, действию, деталям..." 
                                    value={searchQuery} 
                                    onChange={e => setSearchQuery(e.target.value)} 
                                    style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb', outline: 'none' }}
                                />
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <Calendar size={18} color="#6b7280" />
                                <input 
                                    type="date" 
                                    style={{ border: 'none', background: 'transparent', outline: 'none', color: '#374151', fontSize: '0.95rem', cursor: 'pointer' }}
                                    value={filterDate}
                                    onChange={(e) => setFilterDate(e.target.value)}
                                />
                                {filterDate && (
                                    <button onClick={() => setFilterDate('')} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.8rem', cursor: 'pointer', paddingLeft: '8px', borderLeft: '1px solid #e5e7eb', marginLeft: '8px' }}>
                                        Сбросить
                                    </button>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                            <CustomSearchSelect 
                                value={filterRole} 
                                onChange={setFilterRole} 
                                options={roleOptions} 
                                placeholder="Все роли" 
                            />
                            <CustomSearchSelect 
                                value={filterAction} 
                                onChange={setFilterAction} 
                                options={actionOptions} 
                                placeholder="Все действия" 
                            />
                            {user?.role !== 'chief_doctor' && (
                                <CustomSearchSelect 
                                    value={filterHospital} 
                                    onChange={setFilterHospital} 
                                    options={hospitalOptions} 
                                    placeholder="Все больницы" 
                                />
                            )}
                        </div>
                    </div>
                </section>

                <section className="card">
                    <div className="card-header">
                        <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ShieldCheck size={20} color="#9333ea"/> История действий
                        </h2>
                        <p className="card-description" style={{ margin: '4px 0 0 0' }}>Полная история действий всех врачей и пациентов в системе</p>
                    </div>
                    
                    <div className="logs-list">
                        {isLoading ? (
                            <p style={{textAlign: 'center', color: '#64748b', padding: '40px'}}>Загрузка логов...</p>
                        ) : visibleLogs.length === 0 ? (
                            <p style={{textAlign: 'center', color: '#64748b', padding: '40px'}}>Ничего не найдено по вашим фильтрам на выбранную дату.</p>
                        ) : (
                            visibleLogs.map(log => {
                                const style = getActionStyle(log.action_type);
                                return (
                                    <div key={log.id} className="log-item">
                                        <div className="log-header">
                                            <div className={`log-icon-wrapper ${style.color}`}>{style.icon}</div>
                                            <div style={{ flex: 1 }}>
                                                <div className="log-title-row">
                                                    <p className="log-title">{log.action_type}</p>
                                                    <span className="badge-role">{log.role || 'Система'}</span>
                                                </div>
                                                <p className="log-desc">{log.action_desc}</p>
                                            </div>
                                        </div>

                                        <div className="log-meta">
                                            <div className="user-info">
                                                <div className="avatar">{getInitials(log.user_name)}</div>
                                                <div className="user-details">
                                                    <p className="user-name">{log.user_name || 'Неизвестный'}</p>
                                                    <p className="user-role">{log.user_role || 'Без должности'}</p>
                                                </div>
                                            </div>
                                            {/* Скрываем название больницы в бейджике для Главврача, так как это всегда его больница */}
                                            {log.hospital && user?.role !== 'chief_doctor' && (
                                                <div className="meta-text"><Hospital size={14}/> {log.hospital}</div>
                                            )}
                                            {log.patient && (
                                                <div className="meta-text"><User size={14}/> Пациент: {log.patient}</div>
                                            )}
                                        </div>

                                        <div className="log-footer">
                                            <div className="meta-text"><Calendar size={14}/> {formatDate(log.created_at)}</div>
                                            <div className="meta-text"><Clock size={14}/> {formatTime(log.created_at)}</div>
                                            <div className="meta-text"><Monitor size={14}/> {log.device}</div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </section>

                {!isLoading && visibleLogs.length < filteredLogs.length && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Прокрутите вниз, чтобы загрузить еще...
                    </div>
                )}
            </main>
        </div>
    );
};

export default LogsAction;